import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Badge,
  Box,
  Container,
  Heading,
  HStack,
  Link,
  Stack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  buildUnifiedGallerySections,
  EMBED_COMPONENT,
  extractEmbedBlocks,
  filterContentBlocksForFlow,
  resolveLogbookLayout,
  shouldSplitBodyForLayoutD,
  splitBodyAfterTwoParagraphs,
  STRAPI_COMPONENT,
} from '../../utils/logbookLayout';
import LogbookContentBlocks from './LogbookContentBlocks';
import LogbookEmbedBlock from './LogbookEmbedBlock';
import LogbookHero from './LogbookHero';
import LogbookUnifiedGallery from './LogbookUnifiedGallery';
import LogbookCommentsSection from './LogbookCommentsSection';
import { toPlainText } from '../../utils/toPlainText';
import { renderBlocksRichText, stableJsonKey } from './richTextBlocks';

const formatDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const ACTIVITY_TYPE_LABELS_IT = {
  evento: 'Evento',
  laboratorio: 'Laboratorio',
  gioco_libero: 'Gioco libero',
  torneo: 'Torneo',
  centro_estivo: 'Centro estivo',
  riunione: 'Riunione',
  altro: 'Altro',
};

const hasOptionalActivityMeta = (entry) => {
  if (!entry || typeof entry !== 'object') return false;
  if (entry.activity_type && ACTIVITY_TYPE_LABELS_IT[entry.activity_type])
    return true;
  const n = Number(entry.participants_count);
  if (Number.isFinite(n) && n > 0) return true;
  if (
    entry.event &&
    typeof entry.event.urlTitle === 'string' &&
    entry.event.urlTitle.trim()
  )
    return true;
  if (typeof entry.project_tag === 'string' && entry.project_tag.trim())
    return true;
  return false;
};

/** Etichette tag da mostrare sopra la data (tutte le voci non vuote). */
const tagLabelsAboveDate = (tags) => {
  if (!Array.isArray(tags) || !tags.length) return [];
  const out = [];
  const seen = new Set();
  tags.forEach((raw) => {
    const s = toPlainText(raw).trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  });
  return out;
};

const heroHeightsForLayout = (layout) => {
  switch (layout) {
    case 'B':
      return { base: '320px', md: '400px' };
    case 'C':
      return { base: '300px', md: '380px' };
    case 'D':
      return { base: '260px', md: '360px' };
    case 'E':
      return { base: '240px', md: '360px' };
    default:
      return { base: '240px', md: '360px' };
  }
};

/**
 * @param {{ entry: Record<string, unknown> }} props
 */
const LogbookArticleView = ({ entry }) => {
  const layout = useMemo(() => resolveLogbookLayout(entry), [entry]);
  const gallerySections = useMemo(
    () => buildUnifiedGallerySections(entry),
    [entry]
  );
  const embedBlocks = useMemo(
    () => extractEmbedBlocks(entry?.contentBlocks),
    [entry]
  );
  const splitD = shouldSplitBodyForLayoutD(entry);
  const bodySplit = useMemo(
    () => splitBodyAfterTwoParagraphs(entry?.body || []),
    [entry]
  );
  const isMd = useBreakpointValue(
    { base: false, md: true },
    { fallback: 'base' }
  );
  const magazineOverlap = isMd === false && (layout === 'B' || layout === 'C');
  const stripGalleryMobile = layout === 'C';
  const proseBody = layout === 'E';
  const embedVariant = layout === 'D' ? 'socialHeavy' : 'default';

  const flowBlocks = useMemo(
    () =>
      filterContentBlocksForFlow(entry?.contentBlocks, {
        stripEmbed: splitD,
        stripGalleryBlocks: true,
      }),
    [entry, splitD]
  );

  /** Embed salvati in coda: senza titolo «Social» sparivano dopo la galleria. */
  const { flowBeforeEmbeds, flowEmbedBlocks } = useMemo(() => {
    const list = Array.isArray(flowBlocks) ? flowBlocks : [];
    const idx = list.findIndex(
      (b) => b?.[STRAPI_COMPONENT] === EMBED_COMPONENT
    );
    if (idx < 0) return { flowBeforeEmbeds: list, flowEmbedBlocks: [] };
    return {
      flowBeforeEmbeds: list.slice(0, idx),
      flowEmbedBlocks: list.slice(idx),
    };
  }, [flowBlocks]);

  const heroHeights = heroHeightsForLayout(layout);
  const dateLabel = formatDate(entry.date);
  const articleTagLabels = useMemo(
    () => tagLabelsAboveDate(entry.tags),
    [entry.tags]
  );

  const [shareUrl, setShareUrl] = useState(null);
  useEffect(() => {
    if (!entry?.slug) {
      setShareUrl(null);
      return;
    }
    setShareUrl(
      `${window.location.origin}/logbook/${encodeURIComponent(String(entry.slug))}`
    );
  }, [entry?.slug]);

  return (
    <Box as="main" bg="white" minH="100vh">
      <LogbookHero
        title={toPlainText(entry.title)}
        excerpt={toPlainText(entry.excerpt)}
        dateLabel={dateLabel}
        heroImage={
          entry.heroImage?.url
            ? {
                url: entry.heroImage.url,
                alt:
                  toPlainText(entry.heroImage.alt) || toPlainText(entry.title),
              }
            : null
        }
        heroHeights={heroHeights}
        shareUrl={shareUrl}
      />

      <Box
        bg="white"
        mt={{ base: magazineOverlap ? -10 : 0, md: 0 }}
        borderTopRadius={{ base: magazineOverlap ? '2xl' : '0', md: 0 }}
        boxShadow={{
          base: magazineOverlap ? '0 -4px 24px rgba(0,0,0,0.12)' : 'none',
          md: 'none',
        }}
        position="relative"
        zIndex={1}
      >
        <Container
          maxW={layout === 'E' ? '720px' : '900px'}
          px={{ base: 4, md: 6 }}
          py={{ base: 8, md: 10 }}
        >
          <Stack spacing={4}>
            {articleTagLabels.length ? (
              <HStack flexWrap="wrap" spacing={2} rowGap={2} align="center">
                {articleTagLabels.map((label) => (
                  <Badge
                    key={label}
                    bg="#36a9e1"
                    color="white"
                    borderRadius="full"
                    px={2}
                    py={0.5}
                    fontSize="xs"
                    fontWeight="semibold"
                    letterSpacing="wide"
                    textTransform="none"
                  >
                    {label}
                  </Badge>
                ))}
              </HStack>
            ) : null}
            {isMd && dateLabel ? (
              <Text fontSize="sm" color="gray.500">
                {dateLabel}
              </Text>
            ) : null}
            {toPlainText(entry.excerpt) ? (
              <Text
                fontSize={{ base: 'md', md: 'lg' }}
                color="gray.700"
                lineHeight="tall"
              >
                {toPlainText(entry.excerpt)}
              </Text>
            ) : null}
          </Stack>

          {hasOptionalActivityMeta(entry) ? (
            <HStack
              as="aside"
              aria-label="Dati attività"
              flexWrap="wrap"
              spacing={2}
              rowGap={2}
              mt={4}
              pt={3}
              borderTopWidth="1px"
              borderColor="gray.100"
            >
              {entry.activity_type &&
              ACTIVITY_TYPE_LABELS_IT[entry.activity_type] ? (
                <Badge
                  colorScheme="purple"
                  variant="subtle"
                  fontSize="xs"
                  fontWeight="medium"
                >
                  {ACTIVITY_TYPE_LABELS_IT[entry.activity_type]}
                </Badge>
              ) : null}
              {Number.isFinite(Number(entry.participants_count)) &&
              Number(entry.participants_count) > 0 ? (
                <Text fontSize="sm" color="gray.600">
                  {Number(entry.participants_count)} partecipanti
                </Text>
              ) : null}
              {typeof entry.project_tag === 'string' &&
              entry.project_tag.trim() ? (
                <Text fontSize="sm" color="gray.500">
                  Progetto: {toPlainText(entry.project_tag.trim())}
                </Text>
              ) : null}
              {entry.event?.urlTitle ? (
                <HStack
                  as="span"
                  spacing={1}
                  display="inline-flex"
                  alignItems="baseline"
                  flexWrap="wrap"
                >
                  {typeof entry.event.nome === 'string' &&
                  entry.event.nome.trim() ? (
                    <>
                      <Text
                        as="span"
                        fontSize="sm"
                        color="gray.700"
                        fontWeight="medium"
                      >
                        {toPlainText(entry.event.nome.trim())}
                      </Text>
                      <Text
                        as="span"
                        fontSize="sm"
                        color="gray.400"
                        aria-hidden
                      >
                        ·
                      </Text>
                    </>
                  ) : null}
                  <Link
                    as={RouterLink}
                    to={`/event/${encodeURIComponent(entry.event.urlTitle)}`}
                    fontSize="sm"
                    color="#36a9e1"
                    fontWeight="medium"
                  >
                    Vedi evento
                  </Link>
                </HStack>
              ) : null}
            </HStack>
          ) : null}

          <Box mt={8}>
            {splitD ? (
              <>
                {renderBlocksRichText(bodySplit.intro, { prose: proseBody })}
                <Box
                  as="section"
                  aria-labelledby="logbook-social-heading"
                  mt={10}
                >
                  <Heading
                    id="logbook-social-heading"
                    as="h2"
                    size="sm"
                    color="#3b235a"
                    mb={4}
                    fontWeight="semibold"
                  >
                    Social
                  </Heading>
                  <Stack spacing={4}>
                    {embedBlocks.map((eb) => (
                      <LogbookEmbedBlock
                        key={stableJsonKey('emb', eb)}
                        provider={eb.provider}
                        url={eb.url}
                        caption={eb.caption}
                        variant="socialHeavy"
                      />
                    ))}
                  </Stack>
                </Box>
                {bodySplit.rest.length ? (
                  <Box mt={10}>
                    {renderBlocksRichText(bodySplit.rest, { prose: proseBody })}
                  </Box>
                ) : null}
              </>
            ) : (
              renderBlocksRichText(entry.body || [], { prose: proseBody })
            )}
          </Box>

          <LogbookUnifiedGallery
            sections={gallerySections}
            useStripOnMobile={stripGalleryMobile}
          />

          {flowEmbedBlocks.length > 0 ? (
            <>
              {flowBeforeEmbeds.length > 0 ? (
                <LogbookContentBlocks
                  blocks={flowBeforeEmbeds}
                  embedVariant={embedVariant}
                  blockKeyPrefix="flow-pre"
                  stackMt={10}
                />
              ) : null}
              <Box
                as="section"
                aria-labelledby="logbook-social-after-gallery-heading"
                mt={10}
              >
                <Heading
                  id="logbook-social-after-gallery-heading"
                  as="h2"
                  size="sm"
                  color="#3b235a"
                  mb={4}
                  fontWeight="semibold"
                >
                  Social
                </Heading>
                <LogbookContentBlocks
                  blocks={flowEmbedBlocks}
                  embedVariant={embedVariant}
                  blockKeyPrefix="flow-soc"
                  stackMt={0}
                />
              </Box>
            </>
          ) : (
            <LogbookContentBlocks
              blocks={flowBlocks}
              embedVariant={embedVariant}
              blockKeyPrefix="flow"
            />
          )}

          {entry?.slug ? <LogbookCommentsSection slug={entry.slug} /> : null}
        </Container>
      </Box>
    </Box>
  );
};

export default LogbookArticleView;
